// Marriott GPNS report contracts.
//
// Every row type below carries the FULL field set for its file, in Marriott's
// own order and vocabulary, taken from the Appendix A layout. Fields we have no
// source for are typed nullable and returned null rather than omitted —
// Appendix A §5 states LSPs "shall neither alter the formats nor insert any
// columns", so an empty column is correct and a missing one is not.
//
// Where a field is permanently unsourceable it is marked NO SOURCE with the
// reason, so nobody has to re-derive that later.
// GPNS_REPORT_KEYS, GPNS_REPORT_LABELS and GpnsReportKey live in
// `@wci/wv-db-schemas/reports`, next to the registry that derives
// `report:marriott-gpns:<key>:*` grant ids from them. A key is the middle
// segment of a live grant — renaming one silently revokes access — so it has to
// be defined once, beside the thing that turns it into a permission. It had
// been defined here and in two copies of the report registry. Import from the
// package; this file keeps the row contracts and the GPNS-only helpers below.
import {
  GPNS_REPORT_KEYS,
  type GpnsReportKey
} from '@wci/wv-db-schemas/reports'

/**
 * What a row represents, for the table's own count/empty copy — "12 GPNS
 * Properties" reads as report rows, where "12 Properties" reads as the
 * Properties table. Each report passes its own registry `tableId`, which is
 * what keys its saved views and layout, so the name carries copy only.
 */
export const GPNS_REPORT_RESOURCE: Record<GpnsReportKey, string> = {
  contract: 'GPNS Properties',
  'pending-install': 'GPNS Pending Properties',
  gateway: 'GPNS Gateway Properties',
  helpdesk: 'GPNS Ticket Groups',
  incident: 'GPNS Incidents',
  sla: 'GPNS SLA Rows',
  pricing: 'GPNS Billing Rows',
  assets: 'GPNS Devices'
}

export function isGpnsReportKey(value: string): value is GpnsReportKey {
  return (GPNS_REPORT_KEYS as readonly string[]).includes(value)
}

/**
 * Reports whose rows are bounded by the reporting month.
 *
 * The others — Contract, Pending Install and Asset Inventory — are point-in-time
 * snapshots. `bc.customer` and `devices.wireless_device` hold current state with
 * no history, so asking them for March would still return today's contracts and
 * today's devices.
 */
const PERIOD_SCOPED_REPORTS: readonly GpnsReportKey[] = [
  'gateway',
  'helpdesk',
  'incident',
  'sla',
  'pricing'
]

export function isPeriodScoped(report: string): boolean {
  return (PERIOD_SCOPED_REPORTS as readonly string[]).includes(report)
}

type Str = string | null
type Num = number | null

/**
 * File 1 — Contract Data. 52 fields.
 *
 * Sources (src/server/reports/marriott-gpns/build.ts): contract fields from BC;
 * install start from BC's IMPRUGEN job; AP counts and makes counted from
 * `wireless_device` by zone; circuits from `internet_circuit`; everything else
 * from `network_build`, edited on the property's Configuration page.
 */
export type GpnsContractRow = {
  fileDate: string
  marsha: Str
  contractDate: Str
  contractRenewDate: Str // NO SOURCE — BC records renewal terms, not a renewal date
  contractTerm: Num
  contractExpirationDate: Str
  contractMonthly: Str
  installStartDate: Str
  installCompDate: Str
  uatCompDate: Str
  installationType: Str
  gpnsType: Str
  // RETIRED — GPNS 2026 replaced GPNSVersion with GPNSExpDate. Still loaded so
  // it can come back, but its column is hidden and never exported.
  gpnsVersion: Str
  guestRoomInfra: Str
  brandGuestRoomInfra: Str // NO SOURCE — never recorded in either system
  guestRoomsWired: Num
  gpnsExpDate: Str // certificate expiry from network_build, else InstallCompDate + 5 years
  currScreen: Str
  conferenceToolInstalled: Str
  conferenceToolType: Str
  loadBalancerInstalled: Str
  loadBalancerType: Str
  numCircuits: Num
  primaryCircuitProvider: Str
  primaryCircuitSize: Num
  primaryCircuitType: Str
  primaryCircuitBearerSize: Num
  primaryCircuitCIDR: Str
  secCircuitSize: Num
  secCircuitType: Str
  secCircuitBearerSize: Num // NO SOURCE — no second-circuit bearer is recorded
  secCircuitCIDR: Str // NO SOURCE
  t1BandwidthCap: Num
  t2BandwidthCap: Num
  publicBandwidthCap: Num
  apPlacement: Str
  numAPGuest: Num
  numAPConf: Num
  numAPPublic: Num
  numAPBoH: Num // from `unknown`-zone AP names — back of house is not a stored zone
  brandAPGuest: Str
  brandAPConf: Str
  brandAPPublic: Str
  brandEdgeSwitch: Str
  numEdgeSwitch: Num
  brandCoreSwitch: Str
  numCoreSwitch: Num
  brandFW: Str
  fwPartNo: Str // Marriott's ModelFW: OEM SKU from the price book, else the model
  perRmCost: Num
  confMthFee: Num
  gateway: Str
  localCurrencyCode: Str
}

/** File 1b — Pending Install Data. 16 fields. Same sources as File 1. */
export type GpnsPendingInstallRow = {
  fileDate: string
  marsha: Str
  contractDate: Str
  contractTerm: Num
  contractExpirationDate: Str
  installStartDate: Str
  installationType: Str
  gpnsType: Str
  proposedGPNSExpiry: Str // install + 5 years, on the 1st — hardware EOL dates are not sourced
  apPlacement: Str
  brandAP: Str
  brandEdgeSwitch: Str
  brandCoreSwitch: Str
  brandFW: Str
  modelFW: Str
  gateway: Str
}

/** File 2 — Gateway Data. 18 fields. */
export type GpnsGatewayRow = {
  fileDate: string
  marsha: Str
  guestZoneLT: Num
  guestZoneUT: Num
  // NO DEFINITION — the description cell in Marriott's own layout workbook
  // contains a single backtick. Left null rather than guessed.
  upgrades: Num
  conferenceVLANs: Num
  lobbyPublicVLANs: Num
  laptops: Num
  tablets: Num
  smartphones: Num
  gameConsoles: Num
  other: Num
  ios: Num
  windows: Num
  appleOS: Num
  android: Num
  chromeOS: Num
  otherOS: Num
}

/** File 3 — Helpdesk Data. 3 fields. */
export type GpnsHelpdeskRow = {
  marsha: Str
  ticketRootCause: Str
  ticketCount: number
}

/** File 4 — Incident Data. 7 fields. */
export type GpnsIncidentRow = {
  incidentID: Str
  incidentOpenDate: Str
  incidentCloseDate: Str
  marsha: Str
  incidentSeverity: Num
  outageReason: Str
  // NO SOURCE — sn_customerservice_case.comments is 0% populated in production.
  comments: Str
}

/** File 5 — SLA Compliance Data. 8 fields. */
export type GpnsSlaRow = {
  marsha: Str
  fileDate: string
  slaType: string
  // Only carries a value for CUSTOM1-5, which we do not produce.
  customDesc: Str
  noOccurrences: 'Y' | 'N'
  target: Num
  actual: Num
  creditApplied: 'Y' | 'N'
}

/** File 6 — Pricing Data. 9 fields. */
export type GpnsPricingRow = {
  month: Str
  marsha: Str
  billedGuestRm: Num // BC room_count × network_build.price_per_room
  billedConference: Num // network_build.monthly_conference_fee (flat)
  billedOther: Num // NO SOURCE — invoice lines are not split by charge type
  slaCredit: Num // NO SOURCE — Marriott's own workbook marks this a manual entry
  totalBilled: Num
  comments: Str // NO SOURCE
  localCurrencyCode: Str
}

/** File 7 — Asset Inventory. 14 fields. */
export type GpnsAssetRow = {
  marsha: Str
  deviceName: Str
  serialNumber: Str
  manufacturer: Str
  deviceType: Str
  devicePartNo: Str
  privateIP: Str
  publicIPCIDR: Str // NO SOURCE — NAT range is not held per device
  mac: Str
  status: Str
  supportType: Str // NO SOURCE — support coverage is not recorded per device or property
  contractNo: Str // NO SOURCE
  contractExp: Str // NO SOURCE
  deviceOS: Str
}

export type GpnsReportRow =
  | GpnsContractRow
  | GpnsPendingInstallRow
  | GpnsGatewayRow
  | GpnsHelpdeskRow
  | GpnsIncidentRow
  | GpnsSlaRow
  | GpnsPricingRow
  | GpnsAssetRow

/**
 * Not an Appendix A field: the Atlas customer behind a row's MARSHA, so the web
 * table can link to the property. No column is built from it, so an export
 * never carries it — the MARSHA column exports as plain text.
 */
export type GpnsRowProperty = { customerNo: Str }

export type GpnsReportResponse = {
  /**
   * Which report this payload is. The page uses it to tell a fresh response
   * apart from the previous tab's cached one, so it never paints one report's
   * rows under another report's columns while a fetch is in flight.
   */
  report: GpnsReportKey
  /** Reporting month, YYYY-MM. Snapshot reports echo the month they were run for. */
  period: string
  rows: (GpnsReportRow & GpnsRowProperty)[]
}
